<template>
  <div>
    <v-btn color="primary" outlined @click="openDialog">
      <v-icon left>mdi-currency-usd</v-icon>
      Bulk Edit Pricing
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="900px"
      scrollable
      :fullscreen="$vuetify.breakpoint.xs"
      @click:outside="closeDialog"
    >
      <v-card>
        <v-card-title
          class="d-flex justify-space-between align-center py-3 px-4"
        >
          <div>
            <div class="subtitle-1 font-weight-bold">
              Bulk Edit Trip Pricing
            </div>
            <div class="caption grey--text">
              Step {{ step }} of 2 —
              {{ step === 1 ? "Select trips" : "Set pricing" }}
            </div>
          </div>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Step 1: Select trips -->
        <v-card-text
          v-if="step === 1"
          class="pa-3 pa-sm-5"
          style="max-height: 70vh"
        >
          <!-- Filters -->
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="search"
                label="Search"
                outlined
                dense
                hide-details
                clearable
                placeholder="Location or boat name..."
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="fetchTrips"
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="4">
              <v-dialog v-model="fromDateMenu" max-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="fromDate"
                    label="From Date"
                    outlined
                    dense
                    hide-details
                    clearable
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="fromDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="fromDate"
                  @input="fromDateMenu = false"
                ></v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="6" sm="4">
              <v-dialog v-model="toDateMenu" max-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="toDate"
                    label="To Date"
                    outlined
                    dense
                    hide-details
                    clearable
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="toDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="toDate"
                  @input="toDateMenu = false"
                ></v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="12" sm="4" class="d-flex align-center">
              <v-btn
                color="primary"
                outlined
                :loading="loading"
                @click="fetchTrips"
                block
              >
                Search
              </v-btn>
            </v-col>
          </v-row>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-5">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="searched && trips.length === 0"
            class="text-center py-5"
          >
            <v-icon size="64" color="grey lighten-1">mdi-ferry</v-icon>
            <p class="mt-3 grey--text">No unbooked trips found</p>
          </div>

          <!-- Trip list -->
          <div v-else-if="trips.length > 0" class="mt-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="selectAll"
                  :disabled="selectedTrips.length === trips.length"
                >
                  Select All
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="secondary"
                  @click="deselectAll"
                  :disabled="selectedTrips.length === 0"
                >
                  Deselect All
                </v-btn>
              </div>
              <span class="caption grey--text">
                {{ selectedTrips.length }} of {{ trips.length }} selected
              </span>
            </div>

            <div
              v-for="(group, index) in groupedTrips"
              :key="index"
              class="mb-4"
            >
              <div
                class="d-flex flex-column flex-sm-row justify-sm-space-between align-sm-center mb-2"
                style="gap: 2px"
              >
                <h3 class="subtitle-2 font-weight-bold">{{ group.date }}</h3>
                <v-checkbox
                  v-model="group.allSelected"
                  @change="toggleGroupSelection(group)"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                  label="Select all for this date"
                ></v-checkbox>
              </div>

              <div
                v-for="trip in group.trips"
                :key="trip.id"
                class="trip-item d-flex align-start pa-2 mb-2"
              >
                <v-checkbox
                  v-model="selectedTrips"
                  :value="trip.id"
                  hide-details
                  dense
                  class="mt-1 mr-2 flex-shrink-0"
                  @change="updateGroupSelection(group)"
                ></v-checkbox>
                <div class="flex-grow-1 min-width-0">
                  <div
                    class="body-2 font-weight-medium d-flex align-center flex-wrap"
                    style="gap: 4px"
                  >
                    <span>
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      {{ formatTime(trip.fromTime) }} –
                      {{ formatTime(trip.toTime) }}
                    </span>
                    <span>
                      <v-icon x-small class="mr-1">mdi-ferry</v-icon
                      >{{ trip.boat.name }}
                    </span>
                    <v-chip
                      x-small
                      :color="trip.booked > 0 ? 'orange' : 'grey'"
                      dark
                    >
                      {{ trip.booked || 0 }}/{{ trip.capacity }} booked
                    </v-chip>
                  </div>
                  <div class="caption grey--text mt-1">
                    <v-icon x-small>mdi-map-marker</v-icon>
                    {{ trip.fromLocation.name }} → {{ trip.toLocation.name }}
                  </div>
                  <div class="caption grey--text">
                    Tourist: {{ decimalCurrency(trip.laariPerSeat) }} MVR /
                    {{ decimalCurrency(trip.centPerSeat) }} USD
                  </div>
                  <div
                    v-if="
                      trip.laariPerSeatLocal !== trip.laariPerSeat ||
                      trip.centPerSeatLocal !== trip.centPerSeat
                    "
                    class="caption grey--text"
                  >
                    Local: {{ decimalCurrency(trip.laariPerSeatLocal) }} MVR /
                    {{ decimalCurrency(trip.centPerSeatLocal) }} USD
                  </div>
                </div>
              </div>

              <v-divider
                v-if="index < groupedTrips.length - 1"
                class="my-3"
              ></v-divider>
            </div>
          </div>
        </v-card-text>

        <!-- Step 2: Set pricing -->
        <v-card-text
          v-else-if="step === 2"
          class="pa-3 pa-sm-5"
          style="max-height: 70vh"
        >
          <div class="subtitle-1 font-weight-medium mb-1">
            Set new pricing for {{ selectedTrips.length }} selected trip(s)
          </div>
          <div class="caption grey--text mb-4">
            This updates the trip price. Existing bookings are not affected,
            only new bookings will use the new price.
          </div>

          <div class="subtitle-2 mb-2">Tourist price</div>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model.number="newPricing.usdPerSeat"
                label="Tourist price"
                outlined
                type="number"
                suffix="USD"
                hide-details
                min="0"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="newPricing.mvrPerSeat"
                label="Tourist price"
                outlined
                type="number"
                suffix="MVR"
                hide-details
                min="0"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense class="mt-1">
            <v-col>
              <v-checkbox
                v-model="differentLocalPrice"
                label="Different price for locals"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>

          <div v-if="differentLocalPrice">
            <div class="subtitle-2 mb-2 mt-4">Local price</div>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="newPricing.usdPerSeatLocal"
                  label="Local price"
                  outlined
                  type="number"
                  suffix="USD"
                  hide-details
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="newPricing.mvrPerSeatLocal"
                  label="Local price"
                  outlined
                  type="number"
                  suffix="MVR"
                  hide-details
                  min="0"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3 pa-sm-4">
          <v-row no-gutters style="gap: 8px">
            <v-col cols="12" sm="auto" :order="$vuetify.breakpoint.xs ? 2 : 1">
              <v-btn
                block
                outlined
                @click="step === 1 ? closeDialog() : step--"
              >
                {{ step === 1 ? "Cancel" : "Back" }}
              </v-btn>
            </v-col>
            <v-spacer class="d-none d-sm-block"></v-spacer>
            <v-col cols="12" sm="auto" :order="$vuetify.breakpoint.xs ? 1 : 2">
              <v-btn
                v-if="step === 1"
                block
                color="primary"
                :disabled="selectedTrips.length === 0"
                @click="step = 2"
              >
                Next: Set Pricing
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn
                v-else
                block
                color="primary"
                :disabled="!pricingValid"
                :loading="saving"
                @click="updatePricing"
              >
                Update {{ selectedTrips.length }} Trip(s)
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { decimalCurrency } from "../../helpers";

export default {
  name: "BulkEditPricingDialog",
  props: {
    message: Function,
    errorMessage: Function,
    getTrips: Function,
  },
  data: () => ({
    dialog: false,
    step: 1,
    loading: false,
    saving: false,
    searched: false,
    search: "",
    fromDate: moment().format("YYYY-MM-DD"),
    toDate: null,
    fromDateMenu: false,
    toDateMenu: false,
    trips: [],
    selectedTrips: [],
    groupedTrips: [],
    differentLocalPrice: false,
    newPricing: {
      usdPerSeat: 0,
      mvrPerSeat: 0,
      usdPerSeatLocal: 0,
      mvrPerSeatLocal: 0,
    },
  }),
  computed: {
    pricingValid() {
      if (this.newPricing.usdPerSeat < 0 || this.newPricing.mvrPerSeat < 0)
        return false;
      if (this.differentLocalPrice) {
        if (
          this.newPricing.usdPerSeatLocal < 0 ||
          this.newPricing.mvrPerSeatLocal < 0
        )
          return false;
      }
      return true;
    },
  },
  methods: {
    decimalCurrency,
    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.resetData();
    },
    resetData() {
      this.step = 1;
      this.trips = [];
      this.selectedTrips = [];
      this.groupedTrips = [];
      this.searched = false;
      this.differentLocalPrice = false;
      this.newPricing = {
        usdPerSeat: 0,
        mvrPerSeat: 0,
        usdPerSeatLocal: 0,
        mvrPerSeatLocal: 0,
      };
    },
    fetchTrips() {
      this.loading = true;
      this.searched = false;
      this.trips = [];
      this.selectedTrips = [];
      this.groupedTrips = [];

      const params = new URLSearchParams();
      if (this.search) params.append("search", this.search);
      if (this.fromDate) params.append("fromDate", this.fromDate);
      if (this.toDate) params.append("toDate", this.toDate);

      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/gettripsforpricing?${params.toString()}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.trips = resp.data.trips || [];
          this.groupTripsByDate();
          this.searched = true;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          } else {
            this.errorMessage("Failed to fetch trips");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    groupTripsByDate() {
      const grouped = {};
      this.trips.forEach((trip) => {
        const date = moment(trip.fromTime).format("dddd, D MMMM YYYY");
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(trip);
      });
      this.groupedTrips = Object.keys(grouped).map((date) => ({
        date,
        trips: grouped[date],
        allSelected: false,
      }));
    },
    formatTime(time) {
      return moment(time).format("HH:mm");
    },
    selectAll() {
      this.selectedTrips = this.trips.map((t) => t.id);
      this.updateAllGroupSelections();
    },
    deselectAll() {
      this.selectedTrips = [];
      this.updateAllGroupSelections();
    },
    toggleGroupSelection(group) {
      const ids = group.trips.map((t) => t.id);
      if (group.allSelected) {
        ids.forEach((id) => {
          if (!this.selectedTrips.includes(id)) this.selectedTrips.push(id);
        });
      } else {
        this.selectedTrips = this.selectedTrips.filter(
          (id) => !ids.includes(id),
        );
      }
    },
    updateGroupSelection(group) {
      const ids = group.trips.map((t) => t.id);
      const selectedInGroup = this.selectedTrips.filter((id) =>
        ids.includes(id),
      );
      group.allSelected = selectedInGroup.length === ids.length;
    },
    updateAllGroupSelections() {
      this.groupedTrips.forEach((group) => this.updateGroupSelection(group));
    },
    updatePricing() {
      if (this.selectedTrips.length === 0) return;

      const data = {
        tripIds: this.selectedTrips,
        usdPerSeat: Math.round(this.newPricing.usdPerSeat * 100) / 100,
        mvrPerSeat: Math.round(this.newPricing.mvrPerSeat * 100) / 100,
      };
      if (this.differentLocalPrice) {
        data.usdPerSeatLocal =
          Math.round(this.newPricing.usdPerSeatLocal * 100) / 100;
        data.mvrPerSeatLocal =
          Math.round(this.newPricing.mvrPerSeatLocal * 100) / 100;
      }

      this.saving = true;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/bulkeditseatpricing`,
        method: "POST",
        headers: { Authorization: localStorage.getItem("Authorization") },
        data: JSON.stringify(data),
      })
        .then((resp) => {
          this.message(resp.data.message);
          this.getTrips();
          this.closeDialog();
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          } else {
            this.errorMessage("Failed to update pricing");
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.trip-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.trip-item:hover {
  background-color: #f5f5f5;
}

.min-width-0 {
  min-width: 0;
}
</style>
